"use client";
import { withArticle } from "@/lib/utils";

import { useState, useTransition } from "react";
import Link from "next/link";
import Image from "next/image";
import { PostcodeInput } from "@/app/components/PostcodeInput";
import { isValidPostcodeFormat } from "@/lib/postcode";
import {
  searchNearbyTradespeople,
  type NearbyTradesperson,
} from "@/app/actions/search";

interface PriceRangeProps {
  tradeName: string;
  tradeSlug: string;
  labourLow: number;
  labourHigh: number;
  materialsLow: number;
  materialsHigh: number;
}

export function PricingSection({ pricing }: { pricing: PriceRangeProps | null }) {
  const [postcode, setPostcode] = useState("");
  const [multiplier, setMultiplier] = useState<number | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [results, setResults] = useState<NearbyTradesperson[]>([]);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  if (!pricing) return null;

  const m = multiplier ?? 1;
  const totalLow = Math.round((pricing.labourLow + pricing.materialsLow) * m);
  const totalHigh = Math.round((pricing.labourHigh + pricing.materialsHigh) * m);

  function handleSearch() {
    if (!isValidPostcodeFormat(postcode) || !pricing) return;
    const tradeSlug = pricing.tradeSlug;
    startTransition(async () => {
      setSearchError(null);
      const [searchResult, geoResult] = await Promise.all([
        searchNearbyTradespeople(postcode, tradeSlug),
        fetch(`/api/geocode-multiplier?postcode=${encodeURIComponent(postcode)}`).then(
          (r) => r.ok ? r.json() : null
        ).catch(() => null),
      ]);
      if (geoResult?.multiplier) {
        setMultiplier(geoResult.multiplier);
        setLocationName(geoResult.name ?? null);
      }
      if (searchResult.error) {
        setSearchError(searchResult.error);
      } else {
        setResults(searchResult.results);
      }
      setSearched(true);
    });
  }

  return (
    <>
      {/* Cost Section */}
      <section className="mb-10">
        <h2 className="font-serif text-[22px] md:text-[26px] font-semibold text-charcoal mb-4">
          How Much Will It Cost?
        </h2>
        <div className="bg-white rounded-md border border-stone p-5 md:p-7">
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-[28px] md:text-[34px] font-serif font-bold text-forest">
              {"£"}{totalLow.toLocaleString()} &ndash; {"£"}{totalHigh.toLocaleString()}
            </span>
          </div>
          <p className="text-text-muted text-sm mb-1">
            {multiplier && locationName
              ? `Local estimate for ${locationName}`
              : "National average estimate"}
          </p>
          <p className="text-text-muted text-xs mb-5">
            Labour: {"£"}{Math.round(pricing.labourLow * m).toLocaleString()} &ndash; {"£"}{Math.round(pricing.labourHigh * m).toLocaleString()}
            {" | "}
            Materials: {"£"}{Math.round(pricing.materialsLow * m).toLocaleString()} &ndash; {"£"}{Math.round(pricing.materialsHigh * m).toLocaleString()}
          </p>

          {!multiplier && (
            <div className="border-t border-stone pt-5">
              <p className="text-sm font-medium text-charcoal mb-3">
                Enter your postcode for a local price estimate
              </p>
              <div className="flex gap-2 max-w-sm">
                <div className="flex-1">
                  <PostcodeInput
                    value={postcode}
                    onChange={setPostcode}
                    placeholder="e.g. SW1A 1AA"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={isPending || !isValidPostcodeFormat(postcode)}
                  className="px-5 py-2.5 bg-forest text-white text-sm font-semibold rounded-sm disabled:opacity-50 transition-colors hover:bg-forest-light"
                >
                  {isPending ? "Loading..." : "Get Local Price"}
                </button>
              </div>
            </div>
          )}

          {multiplier && !searched && (
            <div className="border-t border-stone pt-5">
              <p className="text-sm font-medium text-charcoal mb-3">
                Find local {pricing.tradeName.toLowerCase()}s near you
              </p>
              <div className="flex gap-2 max-w-sm">
                <div className="flex-1">
                  <PostcodeInput
                    value={postcode}
                    onChange={setPostcode}
                    placeholder="e.g. SW1A 1AA"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={isPending || !isValidPostcodeFormat(postcode)}
                  className="px-5 py-2.5 bg-forest text-white text-sm font-semibold rounded-sm disabled:opacity-50 transition-colors hover:bg-forest-light"
                >
                  {isPending ? "Searching..." : "Find Tradespeople"}
                </button>
              </div>
            </div>
          )}

          {searchError && (
            <p className="mt-3 text-sm text-red-600">{searchError}</p>
          )}
        </div>
        <p className="text-xs text-text-muted mt-3">
          Prices are estimates based on typical UK rates.
          Actual costs depend on the specific issue, accessibility, and your location.
          All quotes from tradespeople on Tradesfolk are completely free.
        </p>
      </section>

      {/* Tradespeople Section */}
      <section className="mb-10">
        <h2 className="font-serif text-[22px] md:text-[26px] font-semibold text-charcoal mb-4">
          Find {withArticle(pricing.tradeName)}
        </h2>

        {searched && results.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {results.map((tp) => (
              <Link
                key={tp.user_id}
                href={`/tradesperson/${tp.user_id}`}
                className="block bg-white rounded-md border border-stone p-4 no-underline hover:border-sage transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  {tp.photo_url ? (
                    <Image
                      src={tp.photo_url}
                      alt={`${tp.first_name} ${tp.last_name}`}
                      width={40}
                      height={40}
                      className="rounded-full object-cover"
                    />
                  ) : (
                    <span className="w-10 h-10 rounded-full bg-forest text-white text-sm font-bold flex items-center justify-center">
                      {tp.first_name?.charAt(0)}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-charcoal">
                      {tp.first_name} {tp.last_name}
                    </p>
                    {tp.business_name && (
                      <p className="text-xs text-text-muted">{tp.business_name}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-text-muted">
                  {tp.avg_rating > 0 && (
                    <span className="flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#B87333" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      {tp.avg_rating.toFixed(1)} ({tp.review_count})
                    </span>
                  )}
                  {tp.distance_miles != null && (
                    <span>{tp.distance_miles.toFixed(1)} miles away</span>
                  )}
                  {tp.years_experience != null && (
                    <span>{tp.years_experience} yrs exp</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {searched && results.length === 0 && !searchError && (
          <p className="text-sm text-text-muted mb-6">
            No {pricing.tradeName.toLowerCase()}s found near that postcode yet.
            Try broadening your search on the{" "}
            <Link href={`/browse?trade=${pricing.tradeSlug}`} className="text-forest underline">
              browse page
            </Link>.
          </p>
        )}

        {!searched && (
          <p className="text-sm text-text-muted mb-6">
            Enter your postcode above to find local {pricing.tradeName.toLowerCase()}s, or{" "}
            <Link href={`/browse?trade=${pricing.tradeSlug}`} className="text-forest underline">
              browse all {pricing.tradeName.toLowerCase()}s
            </Link>.
          </p>
        )}

        <Link
          href={`/find/${pricing.tradeSlug}`}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest text-white text-sm font-semibold rounded-sm no-underline transition-colors hover:bg-forest-light"
        >
          Find {withArticle(pricing.tradeName)}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </Link>
      </section>
    </>
  );
}
